import { useEffect, useState, useRef } from "react";
import { Dialog } from "@headlessui/react";
import {
  XMarkIcon,
  PrinterIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import ResponseMessage from "../components/common/ResponseMessage";
import InputField from "../components/forms/InputField";
import useMultiFieldFormHandler from "../hooks/useMultiFieldFormHandler";
import api from "../api/axios";
import ActionDialog from "../components/common/ActionDialog";
import useAutoClear from "../hooks/useAutoClear";
import { downloadReport } from "../utils/downloadReport";

type Props = {
  open: boolean;
  onClose: () => void;
  onGenerate?: (params: any) => void;
};

export default function InvoicesPrint({ open, onClose, onGenerate }: Props) {
  const [branches, setBranches] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<
    {
      value: string;
      label: string;
    }[]
  >([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [printSubmitting, setPrintSubmitting] = useState(false);
  const printModeRef = useRef<"normal" | "slip">("normal");

  useAutoClear(backendError, () => setBackendError(null), 4000);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoadingBranches(true);
      setLoadingCurrencies(true);
      try {
        const [bRes, cRes] = await Promise.all([
          api.get("/master/select-branch/"),
          api.get("/shop/currencies/"),
        ]);
        setBranches(Array.isArray(bRes.data) ? bRes.data : []);
        if (Array.isArray(cRes.data)) {
          const opts = cRes.data.map((it: any) => ({
            value: String(it.code ?? it.id ?? it.display ?? ""),
            label: it.display ?? it.code ?? it.id ?? String(it),
          }));
          setCurrencies(opts);
        } else setCurrencies([]);
      } catch (err: any) {
        setBackendError(
          err?.response?.data?.message ||
            "Failed to load branches or currencies."
        );
      } finally {
        setLoadingBranches(false);
        setLoadingCurrencies(false);
      }
    };
    load();
  }, [open]);

  const form = useMultiFieldFormHandler(
    {
      date_from: "",
      date_to: "",
      branch: "",
      currency: "",
    },
    {
      validate: (values) => {
        const errors: Partial<Record<string, string>> = {};
        if (!values.date_from.trim())
          errors.date_from = "Please select a start date.";
        if (!values.date_to.trim())
          errors.date_to = "Please select an end date.";
        if (values.date_from && values.date_to) {
          const a = Date.parse(values.date_from);
          const b = Date.parse(values.date_to);
          if (!isNaN(a) && !isNaN(b) && a > b) {
            errors.date_to = "End date must be the same or after start date.";
          }
        }
        if (!values.currency || !String(values.currency).trim()) {
          errors.currency = "Please select a currency.";
        }
        // branch is required for Invoices report
        if (
          !values.branch ||
          !String(values.branch).trim() ||
          String(values.branch).trim() === "select"
        ) {
          errors.branch = "Please select a branch.";
        }
        return Object.keys(errors).length ? (errors as any) : null;
      },
      onSubmit: async (values) => {
        setBackendError(null);
        setPrintSubmitting(true);
        const params = { ...values } as any;

        if (onGenerate) {
          try {
            onGenerate({ ...params, mode: printModeRef.current });
          } finally {
            setPrintSubmitting(false);
          }
          return;
        }

        const endpoint =
          printModeRef.current === "slip"
            ? `${api.defaults.baseURL}/master/invoices-slip/`
            : `${api.defaults.baseURL}/master/invoices-preview/`;

        try {
          await downloadReport(endpoint, params, "invoices_report", (msg) =>
            setBackendError(msg)
          );
        } catch (err: any) {
          setBackendError(
            err?.response?.data?.message || "Failed to generate report."
          );
        } finally {
          setPrintSubmitting(false);
        }
      },
      useDialog: true,
    }
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="fixed z-50 inset-0 flex items-center justify-center"
    >
      <div className="fixed inset-0 bg-black/70" aria-hidden />
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full z-10 p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        <Dialog.Title className="text-lg font-semibold mb-6 flex items-center gap-2">
          <ClipboardDocumentListIcon className="w-5 h-5 text-teal-600" />{" "}
          Invoices Report
        </Dialog.Title>

        {backendError && (
          <div className="mb-3">
            <ResponseMessage variant="error" message={backendError} />
          </div>
        )}

        <form onSubmit={form.handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <InputField
              id="date_from"
              label="From"
              type="date"
              value={form.values.date_from}
              onChange={(e) => form.handleChange("date_from", e.target.value)}
              error={form.errors.date_from ?? undefined}
              icon={<CalendarDaysIcon className="w-4 h-4 text-teal-600" />}
            />

            <InputField
              id="date_to"
              label="To"
              type="date"
              value={form.values.date_to}
              onChange={(e) => form.handleChange("date_to", e.target.value)}
              error={form.errors.date_to ?? undefined}
              icon={<CalendarDaysIcon className="w-4 h-4 text-teal-600" />}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <InputField
              id="branch"
              label="Branch"
              as="select"
              value={form.values.branch}
              onChange={(e) => form.handleChange("branch", e.target.value)}
              error={form.errors.branch ?? undefined}
              options={branches.map((b: any) => ({
                value: String(b.id ?? b.value ?? b),
                label: b.name ?? b.label ?? String(b),
              }))}
              disabled={loadingBranches}
              icon={<MapPinIcon className="w-4 h-4 text-teal-600" />}
            />

            <InputField
              id="currency"
              label="Currency"
              as="select"
              value={form.values.currency}
              onChange={(e) => form.handleChange("currency", e.target.value)}
              options={currencies}
              disabled={loadingCurrencies}
              error={form.errors.currency ?? undefined}
              icon={<CurrencyDollarIcon className="w-4 h-4 text-teal-600" />}
            />
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <button
              type="button"
              onClick={() => {
                printModeRef.current = "slip";
                form.handleSubmit({ preventDefault: () => {} } as any);
              }}
              disabled={printSubmitting}
              className={`bg-teal-600 text-white px-4 py-2 rounded inline-flex items-center ${
                printSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-teal-700"
              }`}
            >
              {printSubmitting ? (
                "Please wait..."
              ) : (
                <>
                  <PrinterIcon className="w-4 h-4 mr-2" /> Slip
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                printModeRef.current = "normal";
                form.handleSubmit({ preventDefault: () => {} } as any);
              }}
              disabled={printSubmitting}
              className={`bg-teal-600 text-white px-4 py-2 rounded inline-flex items-center ${
                printSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-teal-700"
              }`}
            >
              {printSubmitting ? (
                "Please wait..."
              ) : (
                <>
                  <PrinterIcon className="w-4 h-4 mr-2" /> Normal
                </>
              )}
            </button>
          </div>
        </form>

        <ActionDialog
          isOpen={form.dialogOpen}
          title="Generate Report"
          message={
            printModeRef.current === "slip"
              ? "Generate Invoices slip now?"
              : "Generate Invoices report (preview) now?"
          }
          actionColor="#14b8a6"
          proceedLabel="Generate"
          cancelLabel="Cancel"
          onProceed={() => {
            form.handleProceed();
          }}
          onCancel={() => form.setDialogOpen(false)}
        />
      </div>
    </Dialog>
  );
}
